import { makeStyles } from 'tss-react/mui';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomDropZone, DropZoneHelperText, getInitialFiles, Grid, Typography } from 'web-library';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { attachedFilesService } from '../../../../api';
import { useGetApplicationFilesConfigPassportPhoto } from '../../../../api-refactored/candidate/useGetApplicationFilesConfig';
import {
  AttachedFileType,
  NonNationalExamType,
  UploadDocumentType,
} from '../../../../api/candidate/config/model';
import AttachedFiles from '../../../../components/upload-documents/AttachedFiles';
import { handleFileDelete } from '../../../../pages/accessArrangement/SelectCondition/components/UploadDocuments/components/useUploadFile';
import { convertMB2Byte } from '../../../../utils/FileUtils';
import { FormIds } from '../../../accessArrangment/components/candidate/SelectCondition/components/SelectConditionContent';

const useStyles = makeStyles<{ uploadDocumentType: UploadDocumentType, files: any, draftFiles: any }>()((theme, { uploadDocumentType, files, draftFiles }) =>
({
  header: {
    marginBottom: theme.spacing(2),
  },
  description: {
    marginBottom: theme.spacing(4),
  },
  passportDropzone: {
    '& .MuiDropzoneArea-text': {
      color: uploadDocumentType === UploadDocumentType.PASSPORT_PHOTO &&
          ((files.length === 1 && !files[0].hasError) || (draftFiles && draftFiles.length === 1))
          ? theme.color.text.textDisabled
          : theme.color.link.linkDefault01,
    },
    borderColor: uploadDocumentType === UploadDocumentType.PASSPORT_PHOTO &&
        ((files.length === 1 && !files[0].hasError) || (draftFiles && draftFiles.length === 1))
        ? theme.color.border.borderFocused
        : theme.color.link.linkDefault01,
  },
  disabledDropzone: {
    pointerEvents: (uploadDocumentType === UploadDocumentType.PASSPORT_PHOTO &&
        files.length === 1 &&
        !files[0].hasError) ||
        draftFiles?.length === 1
        ? 'none'
        : 'auto',
    caretColor: 'transparent',
  },
  errString: {
    color: theme.color.state.stateError,
  },
}));

interface UploadPassportPhotoProps {
  files: UploadedFile[];
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  hasError?: boolean;
  isHeader?: boolean;
  submittedFiles?: number;
  applicationType: NonNationalExamType;
  draftFiles?: UploadedFile[];
  setDraftFiles?: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  uploadDocumentType: UploadDocumentType;
  isLoading?: boolean;
  includeUploadedFiles?: boolean;
  attachedFileType?: AttachedFileType;
  isFetchedDraft: boolean;
  hasEditAccess?: boolean;
}

interface UseUploadPassportPhotoProps {
  draftFiles?: UploadedFile[];
  setDraftFiles?: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  files: UploadedFile[];
}

export const useUploadPassportPhoto = ({
  draftFiles,
  setDraftFiles,
  setFiles,
  files,
}: UseUploadPassportPhotoProps) => {
  const [dropzoneKey, setDropzoneKey] = useState<string>('');
  const { mutate: deleteFileMutate } = attachedFilesService.all.useDeleteAppealArrangementFile();

  const onUploadFileDelete = async (file: File, isDraft = false) => {
    // to handle file delete
    if (isDraft && draftFiles && setDraftFiles) {
      handleFileDelete(file, draftFiles, setDraftFiles, deleteFileMutate);
    } else {
      handleFileDelete(file, files, setFiles, deleteFileMutate);
    }

    setDropzoneKey(Date.now().toString());
  };

  return { dropzoneKey, onUploadFileDelete };
};

const onFileUpload = (
  uploadedFiles: File[],
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>
) => {
  setFiles(
    uploadedFiles.map((file) => ({
      file,
      uploaded: false,
      hasError: false,
      pending: false,
    }))
  );
};

const UploadSupportingDocumentPassportPhoto = ({
  files,
  setFiles,
  hasError,
  isHeader,
  submittedFiles,
  applicationType,
  draftFiles,
  setDraftFiles,
  uploadDocumentType,
  isLoading,
  includeUploadedFiles = true,
  attachedFileType = AttachedFileType.DOCUMENT,
  isFetchedDraft,
  hasEditAccess = true,
}: UploadPassportPhotoProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles({ uploadDocumentType, files, draftFiles });

  const { data: applicationFilesConfigs } = useGetApplicationFilesConfigPassportPhoto({
    applicationType,
  });

  const { dropzoneKey, onUploadFileDelete } = useUploadPassportPhoto({
    draftFiles,
    setDraftFiles,
    files,
    setFiles,
  });

  const uploadDocumentTypeIndex = uploadDocumentType === UploadDocumentType.PASSPORT_PHOTO ? 0 : 1;

  const initialFiles = getInitialFiles(files);

  return (
    <>
      {isHeader && (
        <>
          <Typography
            variant="h3"
            id={FormIds.UPLOAD_DOCUMENTS_CONTAINER}
            className={`${classes.header} ${hasError ? classes.errString : ''}`}
          >
            {t('accessArrangement->>SelectCondition.UploadDocumentsHeader')}
          </Typography>
          <Typography
            variant="body1"
            className={`${classes.description} ${hasError ? classes.errString : ''}`}
          >
            {t('accessArrangement->>SelectCondition.UploadDocumentsDescription')}
          </Typography>
        </>
      )}
      {applicationFilesConfigs && isFetchedDraft && (
        <Grid xs={12} sm={12} md={12} lg={12}>
          {hasEditAccess && (
            <>
              <CustomDropZone
                acceptedFiles={applicationFilesConfigs[uploadDocumentTypeIndex].uploadFileTypes}
                filesLimit={applicationFilesConfigs[uploadDocumentTypeIndex].fileNumLimit}
                maxSize={convertMB2Byte(
                  applicationFilesConfigs[uploadDocumentTypeIndex].fileSizeLimit
                )}
                className={
                  uploadDocumentType === UploadDocumentType.PASSPORT_PHOTO
                    ? `${classes.passportDropzone} ${classes.disabledDropzone}`
                    : ``
                }
                key={dropzoneKey}
                initialFiles={initialFiles}
                onChange={(uploadedFiles) => onFileUpload(uploadedFiles, setFiles)}
                onDelete={onUploadFileDelete}
                disabled={isLoading}
              />
              <DropZoneHelperText
                acceptedFileTypes={applicationFilesConfigs[uploadDocumentTypeIndex].uploadFileTypes}
                fileLimit={applicationFilesConfigs[uploadDocumentTypeIndex].fileNumLimit}
                fileSizeLimit={applicationFilesConfigs[uploadDocumentTypeIndex].fileSizeLimit}
                photoDimensionLimit={
                  applicationFilesConfigs[uploadDocumentTypeIndex].dimensionLimit
                }
              />
            </>
          )}
        </Grid>
      )}
      <AttachedFiles
        files={files}
        setFiles={setFiles}
        onUploadFileDelete={onUploadFileDelete}
        draftFiles={includeUploadedFiles ? draftFiles : []}
        downloadableLink
        isLoading={isLoading}
        attachedFileType={attachedFileType}
        hideDelete={!hasEditAccess}
      />
    </>
  );
};
export default UploadSupportingDocumentPassportPhoto;
